import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

import { Box, Spacer, Button, FlexiPagination, Loader, Input } from "@flexisaf/flexibull2";
import { Text } from "@flexisaf/flexibull2/build/typo";
import { Icon } from "@/features/ui/base/icon";
import { useGetUpcomingSenateMeeting } from "@/features/meetings";

import theme from "@/style/theme";
import { appPaths } from "@/app";
import { PageLayout, WithLoader, Select, Section } from "../../ui";
import { ApiError } from "@/types/common";
import { useQueryStateSync } from "@/lib/use-query-state-sync";
import { PaginationAdapterReturn, paginationAdapter } from "@/lib/pagination-adapter";

import { PrayerParams, useGetPrayersQuery } from "./prayer-api";
import { PrayerBox } from "../prayers.ui";
import { prayerCategoryOptions } from "../prayers-ui-options";

import { usePrayerController } from "./use-prayer";
import { Unit } from "@/utils/enums";
import { pageOptions } from "@/utils/object-utils";
import { Privileges, usePrivileges } from "@/features/authorization";
import { getDefaultPrayersFilter } from "./prayer-utils";
import { useResponsiveValue } from "@/lib/use-responsive-value";
import { useNavigate } from "react-router-dom";
import { DownloadAttachmentsModal } from "./download-report-modal";

const IconWrapper = styled.div`
    margin-right: 5px;
`;

const FilterLabelWrapper = styled.div`
    display: flex;
    align-items: center;
`;

export const Prayers = (): React.ReactElement => {
    const navigate = useNavigate();

    // Get user details first (without faculty filtering initially)
    const {
        userDetails: initialUserDetails,
        facultyOptions,
        filteredPrayerStatusOptions,
        loading: initialLoading,
    } = usePrayerController();

    const { queryState, setQueryField, page, setPage, setLimit } = useQueryStateSync<
        PrayerParams,
        PaginationAdapterReturn
    >(getDefaultPrayersFilter(initialUserDetails), paginationAdapter);

    // Get department options based on selected faculty
    const { departmentOptions, loading: departmentLoading } = usePrayerController(
        queryState.facultyId
    );
    const { meeting } = useGetUpcomingSenateMeeting();

    const hasSetDefaultMeetingNumber = useRef<boolean>(false);
    const [meetingId, setMeetingId] = useState<number | null>(null);
    const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
    const handleStatusChange = (status) => setQueryField("status", status);
    const handleCategoryChange = (category) => setQueryField("category", category);

    const handleMeetingChange = (meetingNo) => {
        setQueryField("meetingNumber", meetingNo);
        if (meeting?.number === parseInt(meetingNo)) {
            setMeetingId(meeting.id);
        } else {
            setMeetingId(null);
        }
    };

    const handleDepartmentChange = (departmentId) => setQueryField("departmentId", departmentId);
    const handleFacultyChange = (facultyId) => {
        setQueryField("facultyId", facultyId);
        // Clear department selection when faculty changes
        setQueryField("departmentId", "");
    };

    const {
        data: prayerList,
        isLoading,
        isFetching,
        error,
    } = useGetPrayersQuery({
        ...queryState,
        meetingId: meetingId ?? undefined,
    });

    const { hasPrivilege } = usePrivileges();

    const canInitiatePrayer = hasPrivilege(Privileges.InitiatePrayers);
    const canDownloadReports = hasPrivilege(
        [
            Privileges.InitiatePrayers,
            Privileges.ReadPrayers,
            Privileges.ApprovePrayers,
            Privileges.RejectPrayers,
            Privileges.UpdatePrayers,
        ],
        true
    );
    const buttonHeight = useResponsiveValue({
        sm: undefined,
        md: "51",
    }) as string;

    useEffect(() => {
        if (meeting?.number && !hasSetDefaultMeetingNumber.current && !queryState.meetingNumber) {
            setQueryField("meetingNumber", meeting.number);
            setMeetingId(meeting.id); // Set the meeting ID for creating prayers
            hasSetDefaultMeetingNumber.current = true;
        }
    }, [meeting?.id, meeting?.number, setQueryField, queryState.meetingNumber]);

    // TEMPORARY HACK: Filtering prayer requests as a workaround until the backend issue has been fixed
    const data = useMemo(() => {
        if (!prayerList?.entities) return;
        const entities = prayerList.entities.filter((pr) => {
            if (!queryState.status) return true;
            return pr.status.toLowerCase() === queryState.status.toLowerCase();
        });
        return {
            ...prayerList,
            entities,
        };
    }, [prayerList, queryState]);
    return (
        <PageLayout pageLabel="Prayers">
            <Box>
                <WithLoader isLoading={isLoading} error={error as ApiError} height={"100vh"}>
                    <Box>
                        <Section
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                            }}
                        >
                            <Box
                                style={{
                                    width: "100%",
                                }}
                            >
                                <Box
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        width: "100%",
                                        alignItems: "center",
                                    }}
                                >
                                    <Box>
                                        <Text size="22px" bold>
                                            Prayers
                                        </Text>
                                        <Spacer space={20} />
                                        <Box>
                                            <Text color="#64748B">
                                                Search, share and track prayers
                                            </Text>
                                        </Box>
                                    </Box>
                                </Box>

                                {(initialUserDetails.unit === Unit.Department ||
                                    initialUserDetails.unit === Unit.Faculty) && (
                                        <>
                                            {initialUserDetails.faculty && (
                                                <>
                                                    <Spacer space={20} />
                                                    <Box>
                                                        <Text bold>
                                                            Faculty of {initialUserDetails.faculty.name}
                                                        </Text>
                                                    </Box>
                                                </>
                                            )}
                                            {initialUserDetails.department?.name && (
                                                <>
                                                    <Spacer space={5} />
                                                    <Box>
                                                        <Text color="#64748B">
                                                            Department of{" "}
                                                            {initialUserDetails.department.name}
                                                        </Text>
                                                    </Box>
                                                </>
                                            )}
                                        </>
                                    )}
                            </Box>
                            <div style={{ display: "flex", gap: "16px", alignItems: "center", justifyContent: "flex-end" }}>
                                {canDownloadReports && (
                                    <Button
                                        onClick={() => setIsDownloadModalOpen(true)}
                                        height={buttonHeight}
                                        style={{ minWidth: "200px", whiteSpace: "nowrap" }}
                                    >
                                        <Text size="14px">Download Attachments</Text>
                                    </Button>
                                )}
                                {canInitiatePrayer && (
                                    <Button
                                        disabled={!meetingId || !queryState.meetingNumber}
                                        onClick={() =>
                                            navigate(appPaths.createPrayer, {
                                                state: { meetingId },
                                            })
                                        }
                                        width={160}
                                        height={buttonHeight}
                                    >
                                        New Prayer
                                    </Button>
                                )}
                            </div>
                        </Section>
                        <Spacer space={16} />

                        <Section>
                            <Select
                                placeholder="All Prayers"
                                name="status"
                                label={
                                    <FilterLabelWrapper>
                                        <IconWrapper>
                                            <Icon icon="filter-edit" size={14} />
                                        </IconWrapper>

                                        <Text size="14px" color={theme.PrimaryGreyMid}>
                                            Filter Prayers
                                        </Text>
                                    </FilterLabelWrapper>
                                }
                                options={filteredPrayerStatusOptions}
                                spaceTop
                                style={{ width: "100%" }}
                                onChange={handleStatusChange}
                                value={queryState.status ?? ""}
                            />
                            <Spacer space={20} />
                            <Box
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: "20px",
                                }}
                            >
                                {(initialUserDetails.unit === Unit.Sbc ||
                                    initialUserDetails.unit === Unit.Senate) && (
                                        <Box width="49%">
                                            <Select
                                                placeholder="Select Faculty"
                                                name="facultyId"
                                                label={
                                                    <FilterLabelWrapper>
                                                        <IconWrapper>
                                                            <Icon icon="layer" size={14} />
                                                        </IconWrapper>

                                                        <Text size="14px" color={theme.PrimaryGreyMid}>
                                                            Faculty
                                                        </Text>
                                                    </FilterLabelWrapper>
                                                }
                                                options={facultyOptions ?? []}
                                                spaceTop
                                                style={{ width: "100%" }}
                                                isLoading={initialLoading.isLoadingFaculties}
                                                onChange={handleFacultyChange}
                                                value={queryState.facultyId ?? ""}
                                            />
                                        </Box>
                                    )}

                                {initialUserDetails.unit === Unit.Faculty ||
                                    initialUserDetails.unit === Unit.Sbc ||
                                    initialUserDetails.unit === Unit.Senate ? (
                                    <Box width="49%">
                                        <Select
                                            placeholder="Select Department"
                                            name="departmentId"
                                            label={
                                                <FilterLabelWrapper>
                                                    <IconWrapper>
                                                        <Icon icon="layer" size={14} />
                                                    </IconWrapper>

                                                    <Text size="14px" color={theme.PrimaryGreyMid}>
                                                        Department
                                                    </Text>
                                                </FilterLabelWrapper>
                                            }
                                            options={departmentOptions ?? []}
                                            spaceTop
                                            style={{ width: "100%" }}
                                            isLoading={departmentLoading.isLoadingDepartments}
                                            onChange={handleDepartmentChange}
                                            value={queryState.departmentId ?? ""}
                                        />
                                    </Box>
                                ) : (
                                    <Box width="49%">
                                        <Select
                                            placeholder="Select Category"
                                            name="category"
                                            label={
                                                <FilterLabelWrapper>
                                                    <IconWrapper>
                                                        <Icon icon="layer" size={14} />
                                                    </IconWrapper>

                                                    <Text size="14px" color={theme.PrimaryGreyMid}>
                                                        Category
                                                    </Text>
                                                </FilterLabelWrapper>
                                            }
                                            options={prayerCategoryOptions}
                                            spaceTop
                                            style={{ width: "100%" }}
                                            onChange={handleCategoryChange}
                                            value={queryState.category ?? ""}
                                        />
                                    </Box>
                                )}

                                <Box width="49%">
                                    <FilterLabelWrapper>
                                        <IconWrapper>
                                            <Icon icon="judge" size={14} />
                                        </IconWrapper>

                                        <Text size="14px" color={theme.PrimaryGreyMid}>
                                            Senate Meeting Number
                                        </Text>
                                    </FilterLabelWrapper>
                                    <Input
                                        type="number"
                                        style={{ width: "100%" }}
                                        value={queryState.meetingNumber}
                                        onChange={(e) => handleMeetingChange(e.target.value)}
                                    />
                                </Box>
                            </Box>
                            <Spacer space={20} />

                            <Spacer space={30} />
                            <Text size="16px">Prayers</Text>
                            <Spacer space={5} />
                            {isFetching ? (
                                <Box
                                    display="flex"
                                    style={{
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                >
                                    <Loader size={48} />{" "}
                                </Box>
                            ) : (
                                <PrayerBox prayers={data?.entities} />
                            )}

                            {data && data?.entities?.length > 0 && (
                                <>
                                    <Spacer space={5} />
                                    <FlexiPagination
                                        pageCounts={pageOptions}
                                        total={data?.total}
                                        itemsDisplayed
                                        pageSize={queryState.limit}
                                        changePageSize={({ value }) => setLimit(value)}
                                        onChange={(val: number) => setPage(val)}
                                        current={page}
                                        style={{ maxWidth: "100%" }}
                                    />
                                </>
                            )}
                        </Section>
                    </Box>
                </WithLoader>

                {/* Download Attachments Modal */}
                <DownloadAttachmentsModal
                    isOpen={isDownloadModalOpen}
                    onClose={() => setIsDownloadModalOpen(false)}
                />
            </Box>
        </PageLayout>
    );
};
